import { Injectable } from '@angular/core';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { DrillingHoleInfo } from '@3d-scene/model3d/util/drillinghole/drilling-hole-info';
import { DrillingHoleInfoFactory } from '@3d-scene/model3d/util/drillinghole/drilling-hole-info.factory';
import { DrillingHoleUtils } from '@3d-scene/model3d/util/drillinghole/drilling-hole-utils';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUsageService } from '@article/service/article-usage.service';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ProjectModel } from '@project/model/project.model';
import { Observable, Subject } from 'rxjs';

export interface DrillingHoleNotification {
  action: string,
  articleUsage?: ArticleUsageVO,
  success?: boolean
}

@Injectable({providedIn: 'root'})
export class DrillingHoleService {

  public readonly ACTION_OPEN_DRILLING_HOLE_WIZARD: string = 'openWizard';
  public readonly ACTION_EDIT_DRILLING_HOLE: string = 'editDrillingHole';
  public readonly ACTION_NOTIFY_DRILLING_HOLES_INSERTED: string = 'notifyDrillingHolesInserted';
  public readonly ACTION_NOTIFY_PRESET_COMPLETED: string = 'presetCompleted';

  private drillingHoleNotificationSubject: Subject<DrillingHoleNotification> = new Subject<DrillingHoleNotification>();

  constructor(
    private articleUsageService: ArticleUsageService,
    private projectModel: ProjectModel,
    private drillingHoleInfoFactory: DrillingHoleInfoFactory,
    private containerService: ContainerService,
    private articleContainerUtils: ArticleContainerUtils,
    ) {
  }

  public get drillingHoleNotification$(): Observable<DrillingHoleNotification> {
    return this.drillingHoleNotificationSubject.asObservable();
  }

  public openDrillingHoleWizard(): void {
    const action: DrillingHoleNotification = {action: this.ACTION_OPEN_DRILLING_HOLE_WIZARD};
    this.drillingHoleNotificationSubject.next(action);
  }

  public editDrillingHole(articleUsage: ArticleUsageVO): void {
    const action: DrillingHoleNotification = {
      action: this.ACTION_EDIT_DRILLING_HOLE,
      articleUsage: articleUsage
    }
    this.drillingHoleNotificationSubject.next(action);
  }

  public notifyDrillingHolesInserted(): void {
    const action: DrillingHoleNotification = {action: this.ACTION_NOTIFY_DRILLING_HOLES_INSERTED};
    this.drillingHoleNotificationSubject.next(action);
  }

  public notifyPresetCompleted(success: boolean): void {
    const action: DrillingHoleNotification = {
      action: this.ACTION_NOTIFY_PRESET_COMPLETED,
      success: success
    };
    this.drillingHoleNotificationSubject.next(action);
  }

  public getDrillingHoleInfos(railArticleUsage: ArticleUsageVO, collectAccessories = true): DrillingHoleInfo[] {
    const drillingHoleObjects: DrillingHoleInfo[] = [];
    const childGUIDs: string[] = ArticleUsageUtils.getAcceptingAreaIds(railArticleUsage);
    for (const guid of childGUIDs) {
      const childUsage: ArticleUsageVO = this.projectModel.getArticleUsage(guid);
      const childArticle = this.articleUsageService.getArticleByArticleUsage(childUsage);
      if (childArticle && ArticleUtils.isDrillingHole(childArticle)) {
        const drillingHoleInfo = this.drillingHoleInfoFactory.createForRefresh(childUsage, childArticle);
        drillingHoleObjects.push(drillingHoleInfo);

        if (collectAccessories) {
          drillingHoleInfo.accessoryArticles = this.collectAccessoryArticles(drillingHoleInfo);
        }
      }
    }
    DrillingHoleUtils.sortDrillingHolesByPosition(drillingHoleObjects);

    return drillingHoleObjects;
  }

  private collectAccessoryArticles(drillingHoleInfo: DrillingHoleInfo): ArticleVO[] {
    const drillingHoleContainer = this.containerService.findArticleContainerInGlobal(drillingHoleInfo.usageId);
    const accessories = this.articleContainerUtils.getConnectedArticlesRecursively(drillingHoleContainer!);
    return accessories.map((accessory) => accessory.article);
  }
}
